import React from 'react';
import {Text} from '@priority-software/priority-style-react';
import {Step} from './components/Step/Step';
import {ReminderWindow} from './components/ReminderWindow/ReminderWindow';
import {
    setReminder,
    runWizardStepFromReminder,
    clearReminder,
    clearStepCompletedBy
} from '../../actions/reminders';
import {ReminderTypes} from './enum';
import {getCompletedStageText} from './utils';
import texts from '../../texts';
import styles from './styles.module.scss';
import {successStepLogId} from '../../constants';

type Props = {
    onClose: () => void;
    lastUnfinishedStep: string;
    stepTitle: string;
    prevStepTitle: string;
    stages?: any[];
    reminderType: string;
    stepAssignByFullName?: string;
    prevStepFullUserName?: string;
    activeStageIndex: number;
};

export const EOYRemindersView = (props: Props) => {
    const {
        onClose,
        lastUnfinishedStep,
        stepTitle,
        stages,
        reminderType,
        activeStageIndex,
        prevStepTitle,
        prevStepFullUserName,
        stepAssignByFullName
    } = props;
    const activeStage = stages[activeStageIndex];
    const isFinalStep = lastUnfinishedStep === successStepLogId;

    const onDismiss = async () => {
        await setReminder(lastUnfinishedStep);
        onClose();
    };

    const onAccept = async () => {
        if (reminderType !== ReminderTypes.stageCompleted || isFinalStep) {
            await runWizardStepFromReminder();
            clearReminder(lastUnfinishedStep);
        } else {
            // clear the step completed by user field, to prevent the reminder from showing again
            clearStepCompletedBy();
        }
        onClose();
    };

    const renderContent = () => {
        if (
            reminderType === ReminderTypes.stepAwaiting ||
            reminderType === ReminderTypes.stepAssigned
        ) {
            const parts =
                reminderType === ReminderTypes.stepAssigned
                    ? texts.reminder_step_assign_instructions.split('"XXX"')
                    : texts.step_waiting.split('{step_name}');

            return (
                <Text skin="paragraph-2">
                    {parts[0]}{' '}
                    <Text tag="span" fontWeight="bold" skin="paragraph-2">
                        {stepTitle}
                    </Text>{' '}
                    {parts[1]}
                </Text>
            );
        } else if (reminderType === ReminderTypes.stageCompleted) {
            const stageInfo = getCompletedStageText(
                isFinalStep ? 2 : Math.max(activeStageIndex - 1, 0)
            );

            return (
                <>
                    <Text tag="div" skin="paragraph-2">
                        {texts.reminder_step_completed_instructions1
                            .replace('{step_name}', prevStepTitle)
                            .replace('{user_name}', prevStepFullUserName)}
                    </Text>
                    {!!stageInfo ? (
                        <Text tag="div" skin="paragraph-2">
                            {stageInfo}
                        </Text>
                    ) : null}
                </>
            );
        } else if (reminderType === ReminderTypes.stepCompleted) {
            const textPats = texts.reminder_step_completed_instructions2.split('{step_name}');

            return (
                <>
                    <Text tag="div" skin="paragraph-2">
                        {texts.reminder_step_completed_instructions1
                            .replace('{step_name}', prevStepTitle)
                            .replace('{user_name}', prevStepFullUserName)}
                    </Text>
                    <Text tag="div" skin="paragraph-2">
                        {textPats[0]}{' '}
                        <Text tag="span" fontWeight="bold" skin="paragraph-2">
                            {stepTitle}
                        </Text>{' '}
                        {textPats[1]}
                    </Text>
                </>
            );
        } else if (reminderType === ReminderTypes.wizardStage) {
            return (
                <>
                    <Text skin="paragraph-2">{activeStage.text}</Text>

                    <div className={styles.steps}>
                        {stages.map((stage, index) => {
                            return (
                                <Step
                                    key={stage.name}
                                    text={stage.name}
                                    stepNumber={index + 1}
                                    isCompleted={stage.isComplete}
                                    isActive={stage.isActive}
                                    isDisabled={activeStageIndex < index}
                                />
                            );
                        })}
                    </div>
                </>
            );
        }

        return null;
    };

    const getTitle = () => {
        if (reminderType === ReminderTypes.stageCompleted) {
            const stageIndex = isFinalStep ? 2 : Math.max(activeStageIndex - 1, 0);
            return texts[`reminder_stage_${stageIndex}_title`];
        }
        if (reminderType === ReminderTypes.stepAssigned) {
            return texts.reminder_step_assign_title.replace(
                '{User full name}',
                stepAssignByFullName
            );
        }
        return texts[`reminder_stage_${activeStageIndex}_title`];
    };

    const getConfirmButtonLabel = () => {
        if (
            reminderType === ReminderTypes.stageCompleted &&
            lastUnfinishedStep !== successStepLogId
        ) {
            return texts.done;
        }

        if (
            reminderType === ReminderTypes.stepCompleted ||
            reminderType === ReminderTypes.stageCompleted
        ) {
            return texts.reminder_step_assign_next_btn;
        } else if (reminderType === ReminderTypes.stepAwaiting) {
            return texts.reminder_step_awaiting_next_btn;
        } else if (reminderType === ReminderTypes.stepAssigned) {
            return texts.reminder_step_assign_next_btn;
        }
        return '';
    };

    return (
        <ReminderWindow
            title={getTitle()}
            onAccept={onAccept}
            onDismiss={onDismiss}
            content={renderContent()}
            confirmButtonLabel={getConfirmButtonLabel()}
            showRemindLaterButton={reminderType !== ReminderTypes.stageCompleted}
        />
    );
};
